import React, { useEffect, useMemo, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import PagesIcon from '@atlaskit/icon/core/pages';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';

import {
	BOOLEAN_LITERAL_FALSE,
	IDENTIFIER_ACKNOWLEDGED,
	IDENTIFIER_ASSIGNEE,
	IDENTIFIER_IS_SEEN,
	IDENTIFIER_STATUS,
	LOGICAL_OPERATOR_AND,
	OPERATOR_EQUAL_TO,
	STRING_LITERAL_STATUS_CLOSED,
	STRING_LITERAL_STATUS_OPEN,
} from '@atlassian/eoc-query-editor/tokenizer/constants';

import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM,
	FIXED_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	LEVEL_THREE,
	LEVEL_TWO,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import {
	ENTITY_ID,
	MENU_ID_OPERATIONS,
	MENU_ID_OPS_ALERTS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { toMenuIdFromMenuIdList } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/menu-id/index.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { OpsgenieSavedSearchAri } from '@atlassian/ari/opsgenie/saved-search';
import { useAlertsPopoutStore } from '../../operations-expandable-menu-item/action-buttons/useAlertsPopoutStore.tsx';
import type {
	AlertsMenuItemContent$data,
	AlertsMenuItemContent$key,
} from './__generated__/AlertsMenuItemContent.graphql';
import { SavedSearchActionButtons } from './action-button/index.tsx';
import { StarSection } from './star-section/StarSection.tsx';

const allSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.all-search',

	defaultMessage: 'All',
	description: 'Predefined search query in the alert list navigation.',
});

const openSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.open-search',

	defaultMessage: 'Open',
	description: 'Predefined search query in the alert list navigation.',
});

const closedSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.closed-search',

	defaultMessage: 'Closed',
	description: 'Predefined search query in the alert list navigation.',
});

const unackedSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.unacked-search',

	defaultMessage: 'Unacknowledged',
	description: 'Predefined search query in the alert list navigation.',
});

const notSeenSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.not-seen-search',

	defaultMessage: 'Not seen',
	description: 'Predefined search query in the alert list navigation.',
});

const assignedToMeSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.assigned-to-me-search',

	defaultMessage: 'Assigned to me',
	description: 'Predefined search query in the alert list navigation.',
});

const defaultFiltersMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.default-filters',

	defaultMessage: 'Default filters',
	description:
		'Expandable menu item label displayed in the operations sidebar. When clicked, shows pre-configured filter options for alerts.',
});

const sharedWithMeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.shared-with-me',

	defaultMessage: 'Shared with me',
	description:
		'Expandable menu item label displayed in the operations sidebar. Shows alerts and searches that other users have shared with the current user.',
});

const createdByMeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.created-by-me',

	defaultMessage: 'Created by me',
	description:
		'Expandable menu item label displayed in the operations sidebar. Shows alerts and searches that the current user has created.',
});

// const starredMessage = defineMessage({
// 	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.starred',
// 	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
// 	defaultMessage: 'Starred',
// 	description:
// 		'Menu item label displayed in the operations sidebar. When clicked, shows only alerts that the user has marked as favorites.',
// });

// const allAlertsMessage = defineMessage({
// 	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.all-alerts',
// 	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
// 	defaultMessage: 'All alerts',
// 	description:
// 		'Link label displayed in the operations sidebar navigation. When clicked, shows the complete list of alerts without any filters applied.',
// });

// const myAlertsMessage = defineMessage({
// 	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.my-alerts',
// 	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
// 	defaultMessage: 'My alerts',
// 	description: 'The label of alerts that assigned to me link',
// });

const alertsMessageDefinition = defineMessage({
	id: 'navigation-apps-sidebar-nav4-operations.alerts',

	defaultMessage: 'Alerts',
	description: 'Assets dropdown item to link customer to Operations Alerts page.',
});

export function AlertsMenuItemContent({
	queryRef,
}: {
	queryRef: AlertsMenuItemContent$key;
}): React.JSX.Element {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<AlertsMenuItemContent$key>(
		graphql`
			fragment AlertsMenuItemContent on Query {
				opsgenie @required(action: LOG) {
					savedSearches(cloudId: $cloudId) @required(action: LOG) @optIn(to: "opsgenie-beta") {
						starred @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
						sharedWithMe @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
						createdByMe @required(action: LOG) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
					}
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const [selectedFilter, setSelectedFilter] = useState('');

	const { isSelectedPath } = useSidebarNav4();
	const { sharedWithMe, createdByMe, starred } = data?.opsgenie?.savedSearches || {};
	const { formatMessage } = useIntl();
	const accountId = useAccountId();

	const [savedSearchIdFromURL] = useQueryParam('filter');
	const [viewType] = useQueryParam('view');
	const { icon: pagesIcon } = useFormattedMessageAndIcon(alertsMessageDefinition, PagesIcon);

	const { setLastVisitedSearch } = useAlertsPopoutStore();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const defaultFilters = [
		{ id: 'all', name: formatMessage(allSearchMessage), alertSearchQuery: '' },
		{
			id: 'open',
			name: formatMessage(openSearchMessage),
			alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN}`,
		},
		{
			id: 'closed',
			name: formatMessage(closedSearchMessage),
			alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_CLOSED}`,
		},
		{
			id: 'unacked',
			name: formatMessage(unackedSearchMessage),
			alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN} ${LOGICAL_OPERATOR_AND} ${IDENTIFIER_ACKNOWLEDGED}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
		},
		{
			id: 'notSeen',
			name: formatMessage(notSeenSearchMessage),
			alertSearchQuery: `${IDENTIFIER_IS_SEEN}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
		},
		{
			id: 'assignedToMe',
			name: formatMessage(assignedToMeSearchMessage),
			alertSearchQuery: `${IDENTIFIER_ASSIGNEE}${OPERATOR_EQUAL_TO} "${accountId || 'me'}"`,
		},
	];

	const defaultFilterMenuIds = getParentMenuId(defaultFilters);
	const sharedWithMeMenuIds = getParentMenuId(sharedWithMe, savedSearchIdFromURL);
	const createdByMeMenuIds = getParentMenuId(createdByMe, savedSearchIdFromURL);
	const starredIds = useMemo(() => starred?.map((item) => item.id) ?? [], [starred]);

	useEffect(() => {
		if (!isSelectedPath(MENU_ID_OPS_ALERTS) && selectedFilter !== '') {
			setSelectedFilter('');
		}
	}, [isSelectedPath, selectedFilter]);

	return (
		<UFOSegment name="nav4.sidebar.operations.alerts">
			{/* todo: enable this when starred searches are done.
					<MenuSection>
						<MenuSectionHeading>{formatMessage(starredMessage)}</MenuSectionHeading>
						<MenuLinkItem
							isSelected={selectedFilter === 'allAlerts'}
							elemBefore={pagesIcon}
							href="/jira/ops/alerts"
							onClick={() => setSelectedFilter('allAlerts')}
						>
							{formatMessage(allAlertsMessage)}
						</MenuLinkItem>
						<MenuLinkItem
							isSelected={selectedFilter === 'myAlerts'}
							elemBefore={pagesIcon}
							href="/jira/ops/alerts"
							onClick={() => setSelectedFilter('myAlerts')}
						>
							{formatMessage(myAlertsMessage)}
						</MenuLinkItem>
					</MenuSection> */}
			<StarSection
				starred={starred ?? []}
				viewType={viewType}
				savedSearchIdFromURL={savedSearchIdFromURL}
				setSelectedFilter={setSelectedFilter}
				setLastVisitedSearch={setLastVisitedSearch}
			/>
			<Nav4ExpandableMenuItem
				menuId={defaultFilterMenuIds}
				onExpansionToggle={(hasExpanded) => {
					if (hasExpanded) {
						fireUIAnalytics(createAnalyticsEvent({}), {
							action: 'clicked',
							actionSubject: NAVIGATION_ITEM,
							actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
							attributes: {
								componentType: FIXED_ITEM,
								level: LEVEL_TWO,
								itemId: 'defaultFilters',
								parentItemId: 'alerts',
							},
						});
					}
				}}
			>
				<ExpandableMenuItemTrigger
					testId={getTestId(defaultFilterMenuIds)}
					interactionName="nav4-sidebar-operations-alerts"
				>
					{formatMessage(defaultFiltersMessage)}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					{defaultFilters.map((item) => (
						<Nav4MenuLinkItem
							elemBefore={pagesIcon}
							key={item.id}
							href={`/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}`}
							menuId={ENTITY_ID.ALARM_QUEUE(item.alertSearchQuery)}
							onClick={() => {
								setSelectedFilter(item.id);
								setLastVisitedSearch(
									`/jira/ops/alerts?view=${viewType ?? 'list'}${item.alertSearchQuery ? `&query=${item.alertSearchQuery}` : ''}`,
								);

								fireUIAnalytics(createAnalyticsEvent({}), {
									action: 'clicked',
									actionSubject: NAVIGATION_ITEM,
									actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
									attributes: {
										componentType: FIXED_ITEM,
										level: LEVEL_THREE,
										itemId: item.id,
										parentItemId: 'defaultFilters',
										itemType: 'defaultFilter',
									},
								});
							}}
						>
							{item.name}
						</Nav4MenuLinkItem>
					))}
				</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>

			{sharedWithMe && sharedWithMe.length > 0 && (
				<Nav4ExpandableMenuItem
					menuId={sharedWithMeMenuIds}
					onExpansionToggle={(hasExpanded) => {
						if (hasExpanded) {
							fireUIAnalytics(createAnalyticsEvent({}), {
								action: 'clicked',
								actionSubject: NAVIGATION_ITEM,
								actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
								attributes: {
									componentType: FIXED_ITEM,
									level: LEVEL_TWO,
									itemId: 'sharedWithMe',
									parentItemId: 'alerts',
								},
							});
						}
					}}
				>
					<ExpandableMenuItemTrigger
						testId={getTestId(sharedWithMeMenuIds)}
						interactionName="nav4-sidebar-operations-alerts-shared-with-me"
					>
						{formatMessage(sharedWithMeMessage)}
						{sharedWithMe.some((filter) => isSelectedPath(filter.alertSearchQuery))}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>
						{sharedWithMe.map((item) => {
							const savedSearchId = item.id
								? OpsgenieSavedSearchAri.parse(item.id).savedSearchId
								: '';

							return (
								<Nav4MenuLinkItem
									key={item.id}
									elemBefore={pagesIcon}
									href={`/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}${item.id ? `&filter=${savedSearchId}` : ''}`}
									menuId={ENTITY_ID.ALARM_QUEUE(
										savedSearchIdFromURL ? savedSearchId : item.alertSearchQuery,
									)}
									onClick={() => {
										setSelectedFilter('');
										setLastVisitedSearch(
											`/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}${item.id ? `&filter=${savedSearchId}` : ''}`,
										);

										fireUIAnalytics(createAnalyticsEvent({}), {
											action: 'clicked',
											actionSubject: NAVIGATION_ITEM,
											actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
											attributes: {
												componentType: CUSTOM_ITEM,
												level: LEVEL_THREE,
												itemId: item.id,
												itemType: 'savedSearch',
												parentItemId: 'sharedWithMe',
											},
										});
									}}
									actionsOnHover={
										<SavedSearchActionButtons
											savedSearch={item}
											isStarred={starredIds.includes(item.id)}
											savedSearchType="sharedWithMe"
										/>
									}
								>
									{item.name}
								</Nav4MenuLinkItem>
							);
						})}
					</ExpandableMenuItemContent>
				</Nav4ExpandableMenuItem>
			)}
			{createdByMe && createdByMe.length > 0 && (
				<Nav4ExpandableMenuItem
					menuId={createdByMeMenuIds}
					onExpansionToggle={(hasExpanded) => {
						if (hasExpanded) {
							fireUIAnalytics(createAnalyticsEvent({}), {
								action: 'clicked',
								actionSubject: NAVIGATION_ITEM,
								actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
								attributes: {
									componentType: FIXED_ITEM,
									level: LEVEL_TWO,
									itemId: 'createdByMe',
									parentItemId: 'alerts',
								},
							});
						}
					}}
				>
					<ExpandableMenuItemTrigger testId={getTestId(createdByMeMenuIds)}>
						{formatMessage(createdByMeMessage)}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>
						{createdByMe.map((item) => {
							const savedSearchId = item.id
								? OpsgenieSavedSearchAri.parse(item.id).savedSearchId
								: '';

							return (
								<Nav4MenuLinkItem
									key={item.id}
									elemBefore={pagesIcon}
									href={`/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}${item.id ? `&filter=${savedSearchId}` : ''}`}
									menuId={ENTITY_ID.ALARM_QUEUE(
										savedSearchIdFromURL ? savedSearchId : item.alertSearchQuery,
									)}
									onClick={() => {
										setSelectedFilter('');
										setLastVisitedSearch(
											`/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}${item.id ? `&filter=${savedSearchId}` : ''}`,
										);

										fireUIAnalytics(createAnalyticsEvent({}), {
											action: 'clicked',
											actionSubject: NAVIGATION_ITEM,
											actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
											attributes: {
												componentType: CUSTOM_ITEM,
												level: LEVEL_THREE,
												itemId: item.id,
												itemType: 'savedSearch',
												parentItemId: 'createdByMe',
											},
										});
									}}
									actionsOnHover={
										<SavedSearchActionButtons
											savedSearch={item}
											isStarred={starredIds.includes(item.id)}
											savedSearchType="createdByMe"
										/>
									}
								>
									{item.name}
								</Nav4MenuLinkItem>
							);
						})}
					</ExpandableMenuItemContent>
				</Nav4ExpandableMenuItem>
			)}
		</UFOSegment>
	);
}

type AlertFilters = NonNullable<
	NonNullable<NonNullable<AlertsMenuItemContent$data>['opsgenie']>['savedSearches']
>['createdByMe'];

function getParentMenuId(arr?: AlertFilters, savedSearchId?: string) {
	return toMenuIdFromMenuIdList(
		(arr || []).map((i) =>
			ENTITY_ID.ALARM_QUEUE(
				savedSearchId ? OpsgenieSavedSearchAri.parse(i.id).savedSearchId : i.alertSearchQuery,
			),
		),
	);
}
